import { Provider } from '../types/analysis';
import { AssessmentResult } from '../types/assessment';

// Centralized API URL resolution
export function getApiUrl(): string {
  let url = (window as any).__APP_CONFIG__?.BACKEND_API_URL || import.meta.env.VITE_API_URL || '';
  
  if (!url || url === '/') {
    url = '/api';
  }

  url = url.replace(/\/$/, '');
  if (!url.includes('/api')) {
    url = `${url}/api`;
  }
  return url;
}

export const API_URL = getApiUrl();

console.log('Runtime config API URL:', (window as any).__APP_CONFIG__?.BACKEND_API_URL);  
console.log('Environment variable API URL:', import.meta.env.VITE_API_URL);
console.log('Using API URL:', API_URL);

/** Trust Index v2.5 HYBRID assessment via streaming SSE */
export async function assessContent(
  input: string,
  inputType: 'url' | 'text',
  provider: Provider,
  onProgress: (message: string) => void,
  token?: string | null
): Promise<AssessmentResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}/assess/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ input, inputType, provider }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Assessment failed');
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response stream');

  const decoder = new TextDecoder();
  let buffer = '';
  let result: AssessmentResult | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload) continue;

      try {
        const event = JSON.parse(payload);
        if (event.type === 'progress') {
          onProgress(event.message);
        } else if (event.type === 'result') {
          result = event.data as AssessmentResult;
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue; // partial JSON, skip
        throw e;
      }
    }
  }

  if (!result) throw new Error('No assessment result received');
  return result;
}

export async function healthCheck(): Promise<{ status: string }> {
  const response = await fetch(`${API_URL}/health`);
  return response.json();
}

// ─── User management (super admin only) ──────────────────────────────────

export type Role = 'user' | 'contributor' | 'admin';

export interface AppUserDTO {
  uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  role: Role;
  notes: string | null;
  created_at: number;
  last_login_at: number;
}

function authHeaders(token: string | null | undefined): Record<string, string> {
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export async function listUsers(token: string | null): Promise<AppUserDTO[]> {
  const response = await fetch(`${API_URL}/users`, { headers: authHeaders(token) });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Failed to list users (${response.status})`);
  }
  const data = await response.json();
  return data.users || [];
}

export async function updateUser(
  token: string | null,
  uid: string,
  patch: { role?: Role; notes?: string | null },
): Promise<AppUserDTO> {
  const response = await fetch(`${API_URL}/users/${uid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Failed to update user (${response.status})`);
  }
  return response.json();
}

export async function deleteUser(token: string | null, uid: string): Promise<void> {
  const response = await fetch(`${API_URL}/users/${uid}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || `Failed to delete user (${response.status})`);
  }
}
